import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  MessageCircle, 
  Send, 
  Loader2, 
  X, 
  Minimize2, 
  Maximize2, 
  Sparkles,
  CheckCircle,
  AlertCircle,
  Bot
} from "lucide-react";
import { invokeFunction } from "@/integrations/supabase/client";
import { supabase } from "@/integrations/supabase/client";
import { useCourseContext } from "@/contexts/CourseContext";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { AtMentionInput, extractMentionedLabels, type MentionableItem } from "@/components/AtMentionInput";

interface Message {
  role: "user" | "assistant";
  content: string;
  action?: {
    type: string;
    status: "pending" | "success" | "error";
    details?: string;
  };
}

interface PalaceSource {
  title: string;
  url: string;
  asOf: string;
  snippet: string;
}

interface MemoryPalace {
  worldState?: {
    summary: string;
    asOf: string;
    sources: PalaceSource[];
    topicHint?: string;
  };
}

const DESIGNER_WELCOME = `Welcome! What do you want to do?`;
const LEARNER_WELCOME = `Welcome! What do you want to do?`;

const CourseAssistant = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { courseId, courseName, nodeId, nodeName, pageType, workspaceId, workspaceName } = useCourseContext();
  const { toast } = useToast();

  // Seamless handoff from course creation: open assistant when landing on course from creation flow
  useEffect(() => {
    const fromCreation = (location.state as { fromCreation?: boolean })?.fromCreation;
    if (fromCreation && location.pathname.startsWith("/project/")) {
      setIsOpen(true);
      setIsMinimized(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  // Designer: /project, /dashboard, /workspace, /instructor, or /learn with ?preview=designer
  // Learner: /learn without preview=designer
  const isDesignerMode = !location.pathname.startsWith("/learn") || searchParams.get("preview") === "designer";
  
  const [isOpen, setIsOpen] = useState(pageType === "dashboard");
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: isDesignerMode ? DESIGNER_WELCOME : LEARNER_WELCOME }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [memoryPalace, setMemoryPalace] = useState<MemoryPalace>({});
  const [materials, setMaterials] = useState<{ id: string; file_name: string; text_content: string | null; summary: string | null }[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Fetch course materials for @ mention when on a course
  useEffect(() => {
    if (!courseId) {
      setMaterials([]);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("id, file_name, text_content, summary")
        .eq("project_id", courseId);
      if (!error && data) {
        setMaterials(data);
      }
    })();
  }, [courseId]);

  const mentionItems: MentionableItem[] = materials.map((m) => ({
    id: m.id,
    label: m.file_name || "Untitled",
    searchLabel: (m.file_name || "").replace(/\.[^.]+$/, ""), // name without extension for search
  }));

  // Open by default when on dashboard
  useEffect(() => {
    if (pageType === "dashboard") {
      setIsOpen(true);
    }
  }, [pageType]);

  // Update welcome message when switching between designer and learner mode (only if first msg is a welcome)
  useEffect(() => {
    setMessages(prev => {
      const first = prev[0];
      const isWelcome = first?.role === "assistant" && 
        (first.content === DESIGNER_WELCOME || first.content === LEARNER_WELCOME);
      if (isWelcome) {
        return [{ ...first, content: isDesignerMode ? DESIGNER_WELCOME : LEARNER_WELCOME }, ...prev.slice(1)];
      }
      return prev;
    });
  }, [isDesignerMode]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Add context message when context changes
  useEffect(() => {
    if (!isOpen) return;
    const hasContextMessage = messages.some(m =>
      m.role === "assistant" && (
        m.content.includes("I can see you're") ||
        m.content.includes("You're on a workspace") ||
        m.content.includes("You're on the create course page")
      )
    );
    if (hasContextMessage || messages.length > 2) return;

    if (courseId) {
      const contextMsg = isDesignerMode
        ? (nodeName
            ? `I can see you're working on **"${nodeName}"** in the course **"${courseName}"**. How can I help?`
            : `I can see you're working on the course **"${courseName}"**. What would you like to modify?`)
        : (nodeName
            ? `I can see you're viewing **"${nodeName}"**. Ask me anything about this lesson!`
            : `I can help answer questions about this course. What would you like to know?`);
      setMessages(prev => [...prev, { role: "assistant", content: contextMsg }]);
    } else if (pageType === "course-creation" && (workspaceId || workspaceName)) {
      setMessages(prev => [...prev, { role: "assistant", content: `You're on the create course page${workspaceName ? ` for **${workspaceName}**` : ""}. Describe your course in the chat on the left, or tell me the topic here and I'll pass it there.` }]);
    } else if (pageType === "workspace" && (workspaceId || workspaceName)) {
      setMessages(prev => [...prev, { role: "assistant", content: `You're on a workspace${workspaceName ? ` (**${workspaceName}**)` : ""}. What would you like to do?` }]);
    }
  }, [isOpen, courseId, courseName, nodeName, isDesignerMode, pageType, workspaceId, workspaceName]);

  const getContextDescription = () => {
    if (nodeId && nodeName) {
      return `Node: ${nodeName}`;
    }
    if (courseId && courseName) {
      return `Course: ${courseName}`;
    }
    if (pageType === "course-creation" && (workspaceId || workspaceName)) {
      return workspaceName ? `Create course · ${workspaceName}` : "Create course";
    }
    if (pageType === "workspace" && (workspaceId || workspaceName)) {
      return workspaceName ? `Workspace: ${workspaceName}` : "Workspace";
    }
    return "No course selected";
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: input };
    const trimmed = input.trim();
    setInput("");
    setIsLoading(true);

    // On create course page: always treat the message as course topic and pass to creation chat
    if (pageType === "course-creation" && isDesignerMode) {
      // Extract short topic (e.g. "sheep") from phrases like "i want to create a course about sheep"
      const topicMatch = trimmed.match(/course\s+about\s+(.+?)(?:\.|$)/i) ||
        trimmed.match(/course\s+on\s+(.+?)(?:\.|$)/i) ||
        trimmed.match(/create\s+(?:a\s+)?course\s+(?:on|about)\s+(.+?)(?:\.|$)/i) ||
        trimmed.match(/creating\s+(?:a\s+)?course\s+(?:on|about)\s+(.+?)(?:\.|$)/i) ||
        trimmed.match(/want to build\s+(?:a\s+)?course\s+(?:on|about)\s+(.+?)(?:\.|$)/i) ||
        trimmed.match(/i want to create\s+(?:a\s+)?course\s+(?:on|about)\s+(.+?)(?:\.|$)/i) ||
        trimmed.match(/want\s+(?:to\s+create\s+)?(?:a\s+)?course\s+(?:about\s+)?(.+?)(?:\.|$)/i) ||
        trimmed.match(/^(?:i\s+)?want\s+(?:a\s+)?course\s+about\s+(.+?)(?:\.|$)/i);
      const topic = topicMatch ? topicMatch[1].trim() : trimmed;
      setMessages(prev => [...prev, userMessage, {
        role: "assistant",
        content: topic ? `Opening the creation flow for **${topic}**…` : "Opening the creation flow…",
      }]);
      setIsLoading(false);
      window.dispatchEvent(new CustomEvent("open-creation-chat", { detail: { topic } }));
      return;
    }

    setMessages(prev => [...prev, userMessage]);

    try {
      let creationOutline: unknown = null;
      if (courseId) {
        const { data } = await supabase
          .from("addie_outlines")
          .select("creation_outline")
          .eq("project_id", courseId)
          .maybeSingle();
        creationOutline = data?.creation_outline ?? null;
      }

      // Extract @-referenced documents and include their content for the AI
      const referencedDocuments: { name: string; content: string }[] = [];
      if (courseId && materials.length > 0) {
        const mentionedLabels = extractMentionedLabels(trimmed);
        const nameWithoutExt = (s: string) => (s || "").replace(/\.[^.]+$/, "").toLowerCase();
        for (const label of mentionedLabels) {
          const labelNorm = label.toLowerCase();
          const mat = materials.find(
            (m) =>
              nameWithoutExt(m.file_name) === labelNorm ||
              (m.file_name || "").toLowerCase().includes(labelNorm) ||
              labelNorm.includes(nameWithoutExt(m.file_name))
          );
          if (mat) {
            const content = (mat.text_content || mat.summary || "").trim();
            if (content) {
              referencedDocuments.push({
                name: mat.file_name || "Document",
                content: content.substring(0, 12000) + (content.length > 12000 ? "\n...[truncated]" : ""),
              });
            }
          }
        }
      }

      const { data, error } = await invokeFunction("course-assistant", {
        body: { 
          messages: messages.filter(m => !m.content.includes("I can see you're") && !m.content.includes("You're on a workspace")).concat(userMessage).slice(1), // Skip welcome and workspace hint
          context: {
            courseId,
            courseName,
            nodeId,
            nodeName,
            pageType,
            workspaceId: workspaceId ?? undefined,
            workspaceName: workspaceName ?? undefined,
            creation_outline: creationOutline,
          },
          mode: isDesignerMode ? "designer" : "learner",
          referencedDocuments: referencedDocuments.length > 0 ? referencedDocuments : undefined,
          memoryPalace,
          worldStatePolicy: { enabled: true, maxAgeMinutes: 120 },
        },
      });

      if (error) throw new Error(error.message || "Failed to send message");
      
      const assistantMessage: Message = { 
        role: "assistant", 
        content: data.message,
        action: data.action
      };
      setMessages(prev => [...prev, assistantMessage]);
      if (data.memoryPalace) {
        setMemoryPalace(data.memoryPalace as MemoryPalace);
      }

      // Show toast for completed actions
      if (data.action?.status === "success") {
        toast({
          title: "Action completed",
          description: data.action.details || "Changes have been applied",
        });
        
        // Trigger a page refresh for any modifying actions
        const modifyingActions = [
          "UPDATE_OUTLINE", "UPDATE_NODE", "UPDATE_CONTENT", "UPDATE_QUIZ", 
          "ADD_NODE", "DELETE_NODE", "MOVE_NODE",
          "GENERATE_CONTENT", "GENERATE_QUIZ", "REGENERATE_SKILL_TREE"
        ];
        if (modifyingActions.includes(data.action.type)) {
          window.dispatchEvent(new CustomEvent("course-content-updated"));
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sorry, I encountered an error. Please try again.",
        action: { type: "error", status: "error", details: error instanceof Error ? error.message : "Unknown error" }
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg z-50 bg-gradient-to-br from-primary to-primary/80 hover:scale-105 transition-transform touch-manipulation"
        size="icon"
        aria-label="Open course assistant"
      >
        <MessageCircle className="h-6 w-6" />
        {courseId && (
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-green-500 border-2 border-background" />
        )}
      </Button>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <Card className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100vw-2rem)] sm:w-72 max-w-sm shadow-xl z-50 border-2">
        <div 
          className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setIsMinimized(false)}
        >
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
              <Bot className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">Course Assistant</p>
              <p className="text-xs text-muted-foreground truncate max-w-[140px]">
                {getContextDescription()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Full chat window - full screen on mobile, floating on desktop
  return (
    <Card className="fixed inset-4 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-96 sm:max-w-[calc(100vw-2rem)] sm:h-[500px] flex flex-col shadow-2xl z-50 border-2 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold">Course Assistant</p>
            <p className="text-xs text-muted-foreground truncate max-w-[180px]">
              {getContextDescription()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsMinimized(true)}>
            <Minimize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsOpen(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Context Badge */}
      {(courseId || pageType === "workspace" || pageType === "course-creation") && (
        <div className="px-3 py-2 border-b bg-muted/30">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={isDesignerMode ? "default" : "secondary"} className="text-xs">
              {isDesignerMode ? "✏️ Designer" : "📖 Learner"}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {pageType === "workspace" ? "📁 Workspace" :
               pageType === "course-creation" ? "✨ Create course" :
               pageType === "course" ? "📚 Course View" : 
               pageType === "node-lesson" ? "📖 Lesson Editor" :
               pageType === "node-exam" ? "📝 Exam Editor" :
               pageType === "node-slides" ? "🎴 Slides Editor" :
               pageType === "dashboard" ? "🏠 Dashboard" :
               pageType === "learner" ? "📖 Lesson" : "📄 Page"}
            </Badge>
            {nodeName && (
              <Badge variant="secondary" className="text-xs truncate max-w-[200px]">
                {nodeName}
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-3" ref={scrollRef}>
        <div className="space-y-3">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-3 py-2 text-sm",
                  message.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                )}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                
                {/* Action status indicator */}
                {message.action && (
                  <div className={cn(
                    "flex items-center gap-1.5 mt-2 pt-2 border-t text-xs",
                    message.action.status === "success" ? "text-green-600 border-green-200" :
                    message.action.status === "error" ? "text-red-600 border-red-200" :
                    "text-muted-foreground border-muted"
                  )}>
                    {message.action.status === "success" ? (
                      <CheckCircle className="h-3.5 w-3.5" />
                    ) : message.action.status === "error" ? (
                      <AlertCircle className="h-3.5 w-3.5" />
                    ) : (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    )}
                    <span>{message.action.details || message.action.type}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl px-3 py-2 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t bg-muted/30">
        <div className="flex gap-2">
          {courseId && mentionItems.length > 0 ? (
            <AtMentionInput
              value={input}
              onChange={setInput}
              onKeyDown={handleKeyDown}
              placeholder={
                isDesignerMode
                  ? "Ask me to make changes... Type @ to reference documents"
                  : "Ask a question about this lesson..."
              }
              disabled={isLoading}
              items={mentionItems}
              className="text-sm"
            />
          ) : (
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={
                courseId
                  ? (isDesignerMode ? "Ask me to make changes..." : "Ask a question about this lesson...")
                  : pageType === "workspace"
                    ? "Tell me what course you want to create..."
                    : pageType === "course-creation"
                      ? "Describe the course you want to create..."
                      : "Select a course first..."
              }
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="bg-background text-sm flex-1"
            />
          )}
          <Button 
            onClick={sendMessage} 
            disabled={isLoading || !input.trim()}
            size="icon"
            className="shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
          {courseId && mentionItems.length > 0
            ? 'Type @ to reference course materials — e.g. "Update the outline based on @JPME Description"'
            : isDesignerMode
              ? 'Try: "Make the quiz harder" or "Add more detail to the lesson"'
              : 'Try: "Explain this concept" or "Give me an example"'}
        </p>
        {memoryPalace.worldState?.asOf && (
          <p className="text-[10px] text-muted-foreground mt-1 text-center">
            Live world-state context as of {new Date(memoryPalace.worldState.asOf).toLocaleString()}
          </p>
        )}
      </div>
    </Card>
  );
};

export default CourseAssistant;

